import moment from "moment";

/**
 * Parse filename from Content-Disposition (plain or RFC 5987 filename*).
 * @param {string | undefined} header
 * @returns {string | null}
 */
export function parseContentDispositionFilename(header) {
  if (!header || typeof header !== "string") return null;

  const star = /filename\*=(?:UTF-8''|utf-8'')([^;\s]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].replace(/(^"|"$)/g, ""));
    } catch {
      return star[1].replace(/(^"|"$)/g, "");
    }
  }

  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1];

  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain ? plain[1].replace(/(^"|"$)/g, "") : null;
}

/**
 * Coerce axios / fetch payload into a Blob, or throw if the server returned JSON.
 * @param {unknown} data
 * @param {string | undefined} contentType
 * @returns {Blob}
 */
export function coerceDownloadBlob(data, contentType) {
  if (data instanceof Blob) {
    return data;
  }
  if (data instanceof ArrayBuffer) {
    return new Blob([data], {
      type: contentType || "application/octet-stream",
    });
  }
  if (data == null || data === "") {
    throw new Error("Empty file response from server");
  }
  if (typeof data === "string") {
    return new Blob([data], {
      type: contentType || "application/octet-stream",
    });
  }
  if (typeof data === "object") {
    const msg =
      data.message ||
      data.error ||
      data.msg ||
      (typeof data.error === "object" && data.error?.message) ||
      null;
    throw new Error(
      typeof msg === "string" && msg.trim()
        ? msg.trim()
        : "Download failed: server returned JSON instead of a file"
    );
  }
  throw new Error("Invalid download response");
}

/**
 * If a blob response is actually a JSON error body, parse and throw.
 * @param {Blob} blob
 * @param {string | undefined} contentType
 */
export async function assertBlobIsNotJsonError(blob, contentType) {
  const type = String(blob.type || contentType || "").toLowerCase();
  const looksJson =
    type.includes("application/json") ||
    type.includes("text/json") ||
    (blob.size > 0 && blob.size <= 4096 && type.includes("text/"));

  if (!looksJson) return blob;

  const text = await blob.text();
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return new Blob([text], { type: contentType || blob.type });
  }

  try {
    const parsed = JSON.parse(trimmed);
    const msg =
      parsed?.message ||
      parsed?.error ||
      parsed?.msg ||
      (typeof parsed?.error === "string" ? parsed.error : null);
    throw new Error(
      typeof msg === "string" && msg.trim()
        ? msg.trim()
        : "Download failed"
    );
  } catch (err) {
    if (err instanceof Error && err.message !== "Download failed") {
      throw err;
    }
    return new Blob([text], { type: contentType || blob.type });
  }
}

/**
 * @param {Blob} blob
 * @param {string} filename
 */
export function triggerBlobDownload(blob, filename) {
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid file data received from server");
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Fallback when Content-Disposition is missing: {event_name}_{start_time}.{ext}
 * @param {string} eventTitle
 * @param {string | Date | undefined} startdatetime
 * @param {"xlsx" | "csv"} format
 */
export function buildEventSummaryFallbackFilename(
  eventTitle,
  startdatetime,
  format = "xlsx"
) {
  const ext = format === "csv" ? "csv" : "xlsx";
  const slug = String(eventTitle || "event")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const name = slug || "event";

  let timePart = "";
  if (startdatetime != null && startdatetime !== "") {
    const m = moment(startdatetime);
    if (m.isValid()) {
      timePart = `_${m.format("DD-MM-YYYY_HH-mm-ss")}`;
    }
  }

  return `${name}${timePart}.${ext}`;
}

/**
 * Guestlist template: {event_slug}_guestlist.{xlsx|csv}
 * @param {string} eventTitle
 * @param {"xlsx" | "csv"} [format]
 */
export function buildGuestlistTemplateFilename(eventTitle, format = "xlsx") {
  const ext = format === "csv" ? "csv" : "xlsx";
  const slug = String(eventTitle || "event")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${slug || "event"}_guestlist.${ext}`;
}
